import React from 'react';
import c from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { TableRender, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import type { ParagraphScores as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

export interface ParagraphScoresProps {
  entity: ImmutableEntity<Type>;
}

function mapParagraphsToTableHead(paragraphs: Type['field_paragraphs']) {
  const labels = paragraphs.flatMap((paragraph) => (
    paragraph.entity.field_rating.map((rating) => rating.label)
  ));

  return Array.from(new Set(labels));
}

function mapParagraphsToTableRows(paragraphs: Type['field_paragraphs']) {
  return paragraphs.map((paragraph) => ({
    head: paragraph.entity.field_title[0].value,
    columns: paragraph.entity.field_rating,
  }));
}

const ParagraphScores: React.FC<ParagraphScoresProps> = ({ entity }) => {
  const paragraph = entity.toJS();
  const title = paragraph.field_title[0].value;
  const body = paragraph.field_body[0].processed;
  const ratings = paragraph.field_paragraphs;

  const classNames = c('Paragraph', 'ParagraphScores');

  const tableHead = mapParagraphsToTableHead(ratings);
  const tableRows = mapParagraphsToTableRows(ratings);

  return (
    <div className={classNames}>
      <h2 className="asH1">
        {title}
      </h2>

      <div className="ParagraphScores__container">
        <TableRender
          mobileCardsBreakpoint={768}
          transposeOnMobile
          element={(
            <table>
              <thead>
                <tr>
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <th>&nbsp;</th>
                  {tableHead.map((column) => (
                    <th key={`table-head-${column}`}>
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={`table-row-${row.head}`}>
                    <th>{row.head}</th>

                    {row.columns.map((column) => {
                      const checkmarkClassNames = c('ParagraphScores__checkmark', {
                        'ParagraphScores__checkmark--checked': column.selected,
                      });

                      return (
                        <td key={`table-column-${column.value}`}>
                          <div className="ParagraphScores__checkmark-container">
                            <div className={checkmarkClassNames} aria-label={column.selected ? 'selected' : 'not selected'}>
                              <div className="icon icon-check" />
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        />

        <div className="ParagraphScores__body-wrapper">
          <WysiwygField
            className="ParagraphScores__body"
            property="schema:text"
            html={body}
          />
        </div>
      </div>
    </div>
  );
};

export default ParagraphScores;
